import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FrontechLogo from './FrontechLogo';
import {
  Plus, Bot, MessageSquare, Clock, ChevronRight,
  Trash2, Mic, Zap, TrendingUp, PauseCircle, PlayCircle,
} from 'lucide-react';

export default function AgentList() {
  const { api } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api().get('/api/agents');
        setAgents(data);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [api]);

  const deleteAgent = async (id) => {
    if (!window.confirm('Delete this agent? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api().delete(`/api/agents/${id}`);
      setAgents(prev => prev.filter(a => a.id !== id));
    } catch (err) { console.error(err); }
    setDeletingId(null);
  };

  const reactivateAgent = async (id) => {
    setReactivatingId(id);
    try {
      await api().post(`/api/agents/${id}/activate`);
      setAgents(prev => prev.map(a => a.id === id ? { ...a, is_active: true } : a));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not reactivate agent.');
    }
    setReactivatingId(null);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <FrontechLogo size={44} variant="loader" color="var(--color-accent)" />
      <span className="text-xs font-mono tracking-widest uppercase" style={{ color: 'var(--color-text-muted)' }}>
        Loading agents...
      </span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 data-testid="agents-heading" className="font-heading text-2xl font-bold tracking-tight"
            style={{ color: 'var(--color-text-primary)' }}>
            Voice Agents
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Build, train, and deploy AI voice agents to your website
          </p>
        </div>
        <Link
          to="/dashboard/agents/new"
          data-testid="create-agent-btn"
          className="btn-accent shrink-0"
        >
          <Plus size={16} /> New Agent
        </Link>
      </div>

      {/* Empty state */}
      {agents.length === 0 ? (
        <div data-testid="empty-agents"
          className="flex flex-col items-center justify-center py-24 rounded-2xl text-center"
          style={{
            background: 'var(--color-surface)',
            border: '1px dashed var(--color-border)',
          }}>
          <div className="mb-6">
            <FrontechLogo size={52} variant="animated" color="var(--color-accent)" />
          </div>
          <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            No agents yet
          </h3>
          <p className="text-sm mb-8 max-w-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Create your first voice AI agent and deploy it to your website in minutes.
          </p>
          <Link
            to="/dashboard/agents/new"
            className="btn-accent"
          >
            <Plus size={16} /> Create Your First Agent
          </Link>
        </div>
      ) : (
        <>
          {/* Quick stats bar */}
          <div
            className="grid grid-cols-3 gap-4 mb-6 p-5 rounded-2xl"
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
            }}
          >
            {[
              {
                icon: Bot,
                label: 'Total Agents',
                value: agents.length,
                color: 'var(--color-accent)',
              },
              {
                icon: MessageSquare,
                label: 'Total Messages',
                value: agents.reduce((s, a) => s + (a.total_messages || 0), 0).toLocaleString(),
                color: '#4193F0',
              },
              {
                icon: Clock,
                label: 'Minutes Used',
                value: agents.reduce((s, a) => s + (a.total_minutes || 0), 0).toFixed(0),
                color: 'var(--color-frontech-blue)',
              },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <stat.icon size={13} style={{ color: stat.color, opacity: 0.7 }} />
                  <span className="font-heading text-xl font-bold stat-number" style={{ color: 'var(--color-text-primary)' }}>
                    {stat.value}
                  </span>
                </div>
                <span className="text-xs font-mono uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>

          {/* Agent cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {agents.map((agent, idx) => {
              const isActive = (agent.total_messages || 0) > 0;
              const isPaused = agent.is_active === false;
              return (
                <div
                  key={agent.id}
                  data-testid={`agent-card-${agent.id}`}
                  className="card-hover-glow group flex flex-col rounded-2xl p-5"
                  style={{
                    background: 'var(--color-surface)',
                    border: isPaused ? '1px solid rgba(245,158,11,0.35)' : '1px solid var(--color-border)',
                    animationDelay: `${idx * 0.05}s`,
                    opacity: isPaused ? 0.85 : 1,
                  }}
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Agent avatar */}
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300"
                        style={{
                          background: `${agent.widget_color || '#00D67E'}18`,
                          border: `1px solid ${agent.widget_color || '#00D67E'}28`,
                        }}
                      >
                        <Mic size={19} style={{ color: agent.widget_color || 'var(--color-accent)' }} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>
                            {agent.name}
                          </h3>
                          {isPaused && (
                            <span data-testid={`paused-pill-${agent.id}`}
                              className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider"
                              style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>
                              <PauseCircle size={9} /> Paused
                            </span>
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                          {agent.voice_name || 'Rachel'} voice
                        </p>
                      </div>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => deleteAgent(agent.id)}
                      data-testid={`delete-agent-${agent.id}`}
                      disabled={deletingId === agent.id}
                      className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all cursor-pointer disabled:opacity-30"
                      style={{ color: 'var(--color-text-muted)' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-muted)'}
                      aria-label="Delete agent"
                    >
                      {deletingId === agent.id
                        ? <span className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin block" />
                        : <Trash2 size={15} />
                      }
                    </button>
                  </div>

                  {/* System prompt preview */}
                  <p className="text-xs leading-relaxed line-clamp-2 mb-4 flex-1"
                    style={{ color: 'var(--color-text-secondary)' }}>
                    {agent.system_prompt || 'No system prompt configured'}
                  </p>

                  {/* Stats row */}
                  <div
                    className="flex items-center gap-4 mb-4 py-3 px-3 rounded-xl"
                    style={{ background: 'var(--color-elevated)' }}
                  >
                    <div className="flex items-center gap-1.5">
                      <MessageSquare size={11} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs font-mono stat-number" style={{ color: 'var(--color-text-secondary)' }}>
                        {(agent.total_messages || 0).toLocaleString()}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>msgs</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={11} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs font-mono stat-number" style={{ color: 'var(--color-text-secondary)' }}>
                        {(agent.total_minutes || 0).toFixed(1)}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>min</span>
                    </div>
                    {/* Activity indicator */}
                    <div className="ml-auto flex items-center gap-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)' }}
                      />
                      <span className="text-xs font-mono" style={{ color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
                        {isActive ? 'active' : 'idle'}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    {isPaused ? (
                      <button
                        onClick={() => reactivateAgent(agent.id)}
                        data-testid={`reactivate-agent-${agent.id}`}
                        disabled={reactivatingId === agent.id}
                        className="flex items-center gap-1 text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50"
                        style={{ color: '#F59E0B' }}
                      >
                        <PlayCircle size={13} /> {reactivatingId === agent.id ? 'Reactivating…' : 'Reactivate'}
                      </button>
                    ) : (
                      <Link
                        to={`/dashboard/agents/${agent.id}`}
                        data-testid={`view-agent-${agent.id}`}
                        className="flex items-center gap-1 text-xs font-semibold transition-colors cursor-pointer"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        Manage Agent <ChevronRight size={13} />
                      </Link>
                    )}
                    <div className="flex items-center gap-1.5">
                      <TrendingUp size={11} style={{ color: 'var(--color-text-muted)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {agent.total_conversations || 0} convos
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Create new card */}
            <Link
              to="/dashboard/agents/new"
              className="group flex flex-col items-center justify-center rounded-2xl p-6 transition-all duration-200 cursor-pointer min-h-[200px]"
              style={{
                background: 'transparent',
                border: '1px dashed var(--color-border)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(0,214,126,0.3)';
                e.currentTarget.style.background = 'var(--color-accent-dim)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-200"
                style={{ background: 'var(--color-elevated)' }}>
                <Plus size={22} style={{ color: 'var(--color-text-muted)' }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                New Agent
              </span>
              <span className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Add to your workspace
              </span>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
